import { add, addMilliseconds, isAfter, parseISO } from 'date-fns';
import { APP_CONFIG } from '../../constants/APP_CONFIG';
import { memoryStorage } from './memoryStorage';

// For some reason the Storage type extends Record<string, any>
type StrictStorage = Storage & Record<string, never>;
type AppCacheEntry = {
  expireOn?: string;
  obj: any;
};

type CacheContainer = {
  [cacheId: string]:
    | {
        [entryKey: string]: AppCacheEntry | undefined;
      }
    | undefined;
};

const MASTER_KEY = '__SD_CACHE__';

const STORAGE = {
  memory: memoryStorage as StrictStorage,
  localStorage: localStorage as StrictStorage,
  sessionStorage: sessionStorage as StrictStorage
} as const;

export type AppCacheStorageType = keyof typeof STORAGE;
export type AppCache = ReturnType<typeof createCache>;
export type CreateCacheOptions = {
  storageType: AppCacheStorageType;
  defaultTtl?: number | Duration;
};

const getCacheContainer = (storage: StrictStorage): CacheContainer => {
  const serializedObj = storage.getItem(MASTER_KEY) || '{}';
  return JSON.parse(serializedObj);
};

const setCacheContainer = (storage: StrictStorage, obj: CacheContainer) => {
  storage.setItem(MASTER_KEY, JSON.stringify(obj));
  return obj;
};

/**
 * Nukes all values from all cache storages (full wipe)
 */
export const clearAllCache = () => Object.values(STORAGE).forEach(storage => setCacheContainer(storage, {}));

if (localStorage.getItem('__SD_APP_VERSION__') !== APP_CONFIG.build.gitSha) {
  clearAllCache();
  localStorage.setItem('__SD_APP_VERSION__', APP_CONFIG.build.gitSha);
}

export const createCache = (
  cacheId: string,
  { defaultTtl, storageType = 'memory' }: Partial<CreateCacheOptions> = {}
) => {
  const isExpired = (date: string) => isAfter(Date.now(), parseISO(date));
  const expireDate = (durationOrMs: number | Duration) =>
    typeof durationOrMs === 'number' ? addMilliseconds(new Date(), durationOrMs) : add(new Date(), durationOrMs);
  const storage = STORAGE[storageType];

  const cache = {
    /**
     * Removes everything from the cache
     */
    clear() {
      setCacheContainer(storage, {
        ...getCacheContainer(storage),
        [cacheId]: undefined
      });
    },
    /**
     * Force flush of expired cache entries. This is generally done
     * automatically during boot but can be done manually
     * with this call if you have a long-lived cache.
     */
    cleanExpired() {
      const container = getCacheContainer(storage);
      const cleanedContainer = Object.entries(container).reduce(
        (ctnAcc, [cId, entries = {}]) => ({
          ...ctnAcc,
          [cId]: Object.entries(entries).reduce(
            (entriesAcc, [key, entry]) => ({
              ...entriesAcc,
              [key]: entry?.expireOn && isExpired(entry.expireOn) ? undefined : entry
            }),
            entries
          )
        }),
        container
      );
      setCacheContainer(storage, cleanedContainer);
    },
    /**
     * Attempts to retrieve an item from cache, if it does not exist in cache, it will
     * call the provided callback and assign the return value to the cache.
     * The final cached item is returned back
     */
    cacheSync<TEntry>(key: string, callback: () => TEntry, expire = defaultTtl): TEntry {
      const obj = this.get<TEntry>(key);
      return obj === undefined ? this.set(key, callback(), expire) : obj;
    },
    /**
     * Attempts to retrieve an item from cache, if it does not exist in cache, it will
     * call the provided callback and assign the return value to the cache.
     * The final cached item is returned back
     */
    async cacheAsync<TEntry>(key: string, callback: () => Promise<TEntry>, ttl = defaultTtl): Promise<TEntry> {
      const obj = this.get<TEntry>(key);
      return obj === undefined ? this.set(key, await callback(), ttl) : obj;
    },
    /**
     * Determines if the cache contains an entry for a given key
     */
    has(key: string) {
      return this.get(key) !== undefined;
    },
    /**
     * Gets something from the cache
     */
    get<TEntry>(key: string): TEntry | undefined {
      const container = getCacheContainer(storage);
      const entry = container[cacheId]?.[key];
      if (!entry || (entry.expireOn && isExpired(entry.expireOn))) {
        return undefined;
      }
      return entry.obj;
    },
    /**
     * Sets an entry in the cache, the cached item is also returned
     */
    set<TEntry>(key: string, obj: TEntry, ttl = defaultTtl): TEntry {
      const container = getCacheContainer(storage);
      const expireOn = ttl ? expireDate(ttl).toJSON() : undefined;
      setCacheContainer(storage, {
        ...container,
        [cacheId]: {
          ...container[cacheId],
          [key]: { expireOn, obj }
        }
      });
      return obj;
    },
    /**
     * Deletes an entry from the cache if it exists
     */
    delete<TEntry>(key: string): TEntry | undefined {
      const obj = this.get<TEntry>(key);
      const container = getCacheContainer(storage);
      setCacheContainer(storage, {
        ...container,
        [cacheId]: {
          ...container[cacheId],
          [key]: undefined
        }
      });
      return obj;
    },
    /**
     * Returns the number of entries in the cache
     */
    size() {
      const container = getCacheContainer(storage);
      return Object.keys(container[cacheId] || {}).length;
    }
  };

  cache.cleanExpired();
  return cache;
};
